import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { DataService } from '../../config/data/dataService';
import { ClassicGridViewPageVariant } from './QuickActionPageVariants';
import QuickActionModal from './QuickActionModal';
import { mapQuickRequestToApplication } from './quickActionData';

const PAGE_SIZE = 10;

const MyBids = ({ now, questionLabels = {} }) => {
  const [bidRequests, setBidRequests] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [updatingBid, setUpdatingBid] = useState(false);
  const requestIdRef = useRef(0);

  const fetchMyBids = useCallback(async (page = 1, search = searchText, silent = false) => {
    const requestId = ++requestIdRef.current;
    try {
      const response = await DataService.post('/getMyBidList', {
        page: String(page),
        page_items_count: PAGE_SIZE,
        search: search.trim(),
      });
      if (requestId !== requestIdRef.current) return;
      if (response.data.status !== true) {
        throw new Error(response.data.message || 'Unable to fetch your bids');
      }
      const responseData = response.data.data;
      setBidRequests(responseData.data);
      setTotalCount(Number(responseData.total_count) || 0);
      setCurrentPage(Number(responseData.current_page) || page);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error('My bids list error:', error);
      if (!silent) message.error('Unable to fetch your bids');
    }
  }, [searchText]);

  useEffect(() => {
    const searchTimer = setTimeout(() => fetchMyBids(1), 350);
    return () => {
      clearTimeout(searchTimer);
      requestIdRef.current += 1;
    };
  }, [fetchMyBids]);

  useEffect(() => {
    let stopped = false;
    let pollTimer;
    const poll = async () => {
      if (document.visibilityState === 'visible' && !updatingBid) {
        await fetchMyBids(currentPage, searchText, true);
      }
      if (!stopped) pollTimer = setTimeout(poll, 5000);
    };
    pollTimer = setTimeout(poll, 5000);
    return () => {
      stopped = true;
      clearTimeout(pollTimer);
      requestIdRef.current += 1;
    };
  }, [currentPage, fetchMyBids, searchText, updatingBid]);

  const applications = useMemo(() => bidRequests.map((request) => ({
    ...mapQuickRequestToApplication(request, questionLabels),
    date: request.er_created_at || '-',
    bidAmount: request.bid_amount,
  })), [bidRequests, questionLabels]);
  const selectedApp = applications.find((app) => app.id === selectedId);

  const handleUpdateBid = async (payload) => {
    setUpdatingBid(true);
    requestIdRef.current += 1;
    try {
      const response = await DataService.post('/updateExchangeBid', payload);
      if (response.data.status === true) {
        setBidRequests((requests) => requests.map((request) => (
          request.reference_no === payload.reference_no
            ? { ...request, bid_amount: payload.bid_amount }
            : request
        )));
      }
      return response;
    } finally {
      setUpdatingBid(false);
    }
  };

  return (
    <>
      <ClassicGridViewPageVariant
        bidView="submitted"
        filteredApplications={applications}
        searchText={searchText}
        onSearchChange={setSearchText}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          total: totalCount,
          onChange: (page) => {
            setSelectedId(null);
            fetchMyBids(page);
          },
        }}
        onCardClick={setSelectedId}
        now={now}
      />
      <QuickActionModal
        app={selectedApp}
        open={Boolean(selectedApp)}
        onClose={() => setSelectedId(null)}
        bidView="submitted"
        now={now}
        onUpdateBid={handleUpdateBid}
        onBidUpdated={() => fetchMyBids(currentPage, searchText, true)}
      />
    </>
  );
};

export default MyBids;
